import { stringify } from "querystring";

import {
  FormType,
  PaymentMethod,
  FormBackgroundTheme,
  FrontendRoutes,
  DonationFormCategory,
  TicketingFormCategory,
  IsoLocale,
  getDonationOrTicketingFormLink,
  getDonationOrTicketingEmbedLink,
  FormCategory,
  Emptyable,
  ensureAbsoluteUrl,
  hasCumulativeAnnualReceipt,
} from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";
import { DateTime } from "luxon";
import Router from "next/router";

import { getDonationFrequencyFromStripeInterval } from "./donationRecurrence";
import { getChargeDayRenewalDate } from "./getChargeDayRenewalDate";
import { AppRouterType } from "./trpc";

import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { buildMembershipRebuyUrl, MembershipRebuy } from "@/canvas/features/FormV2/helpers/membershipRebuyLink";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { ThankYouPageQueryParams } from "@/canvas/features/ThankYouPage/type";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export type ThankYouPageCommand = Pick<
  NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"],
  "totalAmount" | "tipAmount" | "email" | "paymentMethod" | "productsAmount" | "eligibleAmount"
>;

export type ThankYouPageFormData = {
  id: string;
  path: string;
  campaignId?: string | null;
  teamId?: string | null;
  logoUrl?: string | null;
  isPrimaryCampaign?: boolean;
  isPrimaryTeam?: boolean;
  hasReceipt?: boolean;
};

export type ThankYouPageOrganization = {
  id: string;
  name: string;
  logoUrl?: string | null;
  canGenerateReceipt: boolean;
};

export type ThankYouPageEvent = {
  endTime?: number | null;
};

export interface ThankYouUrlParams {
  command: ThankYouPageCommand;
  formType: FormType;
  organization: ThankYouPageOrganization;
  postTransactionUrl: string | null | undefined;
  isEmbeddedModal: boolean;
  generateETicket: boolean;
  event?: ThankYouPageEvent;
  formData: ThankYouPageFormData;
  isoLocale: IsoLocale;
  category: DonationFormCategory | TicketingFormCategory;
  isAuction: boolean;
  themeColor?: Emptyable<string>;
  isDarkMode: boolean;
  backgroundTheme: FormBackgroundTheme | null;
  isEmbed: boolean;
  embedId?: string | null;
  emailUpdateToken?: string;
  buyerTicketToken?: string;
  membershipRebuy?: MembershipRebuy | null;
  isSweepstakesFreeEntry?: boolean;
  nextPaymentDate?: string | null;
  hasCumulativeReceipt?: boolean;
}

export const getThankYouPageUrl = ({
  command,
  formType,
  organization,
  postTransactionUrl,
  isEmbeddedModal,
  generateETicket,
  event,
  formData,
  isoLocale,
  category,
  isAuction,
  themeColor,
  isDarkMode,
  backgroundTheme,
  isEmbed,
  embedId,
  emailUpdateToken,
  buyerTicketToken,
  membershipRebuy,
  isSweepstakesFreeEntry = false,
  nextPaymentDate,
  hasCumulativeReceipt = false,
}: ThankYouUrlParams): string => {
  const isEmbedV2 = Boolean(embedId);
  const baseShareLink =
    process.env.NEXT_PUBLIC_FRONTEND_URL +
    (isEmbedV2 ? getDonationOrTicketingEmbedLink : getDonationOrTicketingFormLink)({
      form: {
        formCategory: category as FormCategory,
        formType,
        id: formData.id,
        path: formData.path,
        isPrimaryCampaign: formData.isPrimaryCampaign ?? false,
        isPrimaryTeam: formData.isPrimaryTeam ?? false,
      },
      isoLocale,
    });
  const shareLink =
    baseShareLink + (isEmbedV2 && embedId ? `?embed-version=v2&embedId=${encodeURIComponent(embedId)}` : "");

  const params: ThankYouPageQueryParams & Record<string, string | null | undefined | number> = {
    organizationId: organization.id,
    organizationName: organization?.name || "",
    logoUrl: formData.logoUrl || organization?.logoUrl || null,
    amount: command.totalAmount - command.tipAmount,
    tipAmount: command.tipAmount,
    formCategory: category || null,
    modal: isEmbeddedModal ? "true" : "false",
    email: command.email || null,
    formType,
    paymentMethod: command.paymentMethod || null,
    shareLink,
    generateTaxReceipt: getGenerateTaxReceipt(formType, formData, organization, command) ? "true" : "false",
    generateETicket: generateETicket && (command.productsAmount || 0) > 0 ? "true" : "false",
    resourceEventEndTime: event?.endTime || null,
    themeColor,
    isDarkMode: isDarkMode ? "true" : "false",
    backgroundTheme: backgroundTheme || undefined,
    isEmbed: isEmbed ? "true" : "false",
    emailUpdateToken: isAuction ? null : emailUpdateToken || null,
    donatedFormIds: [formData.id, formData.teamId, formData.campaignId].filter(Boolean).join(","),
    ...(buyerTicketToken ? { buyerTicketToken } : {}),
    ...(membershipRebuy && !isEmbedV2 ? { rebuyUrl: buildMembershipRebuyUrl(shareLink, membershipRebuy) } : {}),
    ...(isEmbedV2 && embedId ? { embedId } : {}),
    ...(isSweepstakesFreeEntry ? { sweepstakesFreeEntry: "true" as const } : {}),
    ...(nextPaymentDate ? { nextPaymentDate } : {}),
    ...(hasCumulativeReceipt ? { hasCumulativeReceipt: "true" as const } : {}),
  };

  const thankYouPath = isEmbedV2 ? FrontendRoutes.EmbedThankYou : FrontendRoutes.ThankYou;
  return ensureAbsoluteUrl(postTransactionUrl) || `/${isoLocale}${thankYouPath}?${stringify(params)}`;
};

const isInternalThankYouPage = (url: string): boolean => {
  if (!url.startsWith("/")) {
    return false;
  }
  return url.includes(FrontendRoutes.ThankYou) || url.includes(FrontendRoutes.EmbedThankYou);
};

const isCrossOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

const navigateTopWindow = (url: string): boolean => {
  if (!window.top || window.self === window.top) {
    return false;
  }
  try {
    window.top.location.href = url;
    return true;
  } catch {
    return false;
  }
};

export const navigateAfterPayment = async (url: string): Promise<void> => {
  if (isInternalThankYouPage(url)) {
    await Router.push(url);
    return;
  }
  if (isCrossOriginUrl(url) && navigateTopWindow(url)) {
    return;
  }
  window.location.href = url;
};

const getGenerateTaxReceipt = (
  formType: FormType,
  formData: ThankYouPageFormData,
  organization: ThankYouPageOrganization,
  command: ThankYouPageCommand
) => {
  if (command.paymentMethod === PaymentMethod.Daf || command.paymentMethod === PaymentMethod.Cheque) {
    return false;
  }
  if (formType === FormType.DonationForm && !formData.hasReceipt) {
    return false;
  }

  return (command.eligibleAmount || 0) > 0 && organization.canGenerateReceipt;
};

export const hasCumulativeReceiptPayment = (paymentInput: TicketingPaymentInput | DonationFormPaymentInput): boolean =>
  "recurrenceInterval" in paymentInput &&
  hasCumulativeAnnualReceipt(getDonationFrequencyFromStripeInterval(paymentInput.recurrenceInterval));

export const getNextPaymentDateParam = (
  paymentInput: TicketingPaymentInput | DonationFormPaymentInput
): string | null => {
  if (!("chargeDay" in paymentInput) || !paymentInput.chargeDay) {
    return null;
  }
  const nextPaymentDate = getChargeDayRenewalDate(paymentInput.recurrenceInterval, paymentInput.chargeDay);
  return nextPaymentDate ? DateTime.fromJSDate(nextPaymentDate).toISODate() : null;
};
